// Fulfillment photo gallery — Phase 3 (2026-10-03).
//
// Grid of thumbnails for one procedure fulfillment, with inline add + per-
// photo delete. Replaces the single-thumbnail block in
// ProcedureFulfillmentsPanel: a wound-dressing nurse may legitimately
// upload a before/during/after sequence against one session.
//
// Delete semantics: soft-delete DB row + hard-delete Storage object
// server-side (see modules/db/fulfillment_photos.softDeletePhoto). The
// signed GET URL has 7-day TTL — leaving the blob after a wrong-patient
// mis-attach is the leak path we're closing. The confirm step is a plain
// window.confirm so the nurse can't miss what the X does.

import { useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { FulfillmentPhotoView } from '@pamosi/shared';
import { ApiError, proceduresApi } from '../lib/api';
import { uploadFulfillmentPhoto } from '../lib/photo-upload';

interface Props {
    serviceRequestId: string;
    fulfillmentId: string;
    photos: FulfillmentPhotoView[];
    canEdit: boolean;
    onChanged: () => void;
}

export function FulfillmentPhotoGallery({
    serviceRequestId,
    fulfillmentId,
    photos,
    canEdit,
    onChanged,
}: Props) {
    const inputRef = useRef<HTMLInputElement | null>(null);
    const [uploading, setUploading] = useState(false);
    const [uploadError, setUploadError] = useState<string | null>(null);
    const qc = useQueryClient();

    const deleteMut = useMutation({
        mutationFn: (photoId: string) =>
            proceduresApi.deletePhoto(serviceRequestId, fulfillmentId, photoId),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ['procedure-fulfillments', serviceRequestId] });
            onChanged();
        },
    });

    async function onFile(evt: React.ChangeEvent<HTMLInputElement>) {
        const file = evt.target.files?.[0];
        evt.target.value = '';
        if (!file) return;
        setUploadError(null);
        setUploading(true);
        try {
            await uploadFulfillmentPhoto({
                serviceRequestId,
                fulfillmentId,
                file,
            });
            onChanged();
        } catch (err) {
            setUploadError(
                err instanceof ApiError
                    ? err.message
                    : err instanceof Error ? err.message : 'Photo upload failed.',
            );
        } finally {
            setUploading(false);
        }
    }

    function confirmDelete(photo: FulfillmentPhotoView) {
        const when = new Date(photo.uploaded_at).toLocaleString();
        if (!window.confirm(`Delete photo uploaded ${when}? This cannot be undone.`)) return;
        deleteMut.mutate(photo.id);
    }

    return (
        <div className="mt-2">
            {photos.length > 0 && (
                <div className="flex flex-wrap gap-2">
                    {photos.map((p) => (
                        <div key={p.id} className="relative group">
                            {p.signed_url ? (
                                <a
                                    href={p.signed_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="block rounded-md border border-slate-200 overflow-hidden hover:ring-2 hover:ring-brand-300 transition"
                                    title={`Uploaded by ${p.uploaded_by_full_name ?? 'Nurse'} · ${new Date(p.uploaded_at).toLocaleString()}`}
                                >
                                    <img
                                        src={p.signed_url}
                                        alt="Fulfillment photo"
                                        className="w-16 h-16 object-cover block"
                                        loading="lazy"
                                    />
                                </a>
                            ) : (
                                <div
                                    className="w-16 h-16 rounded-md border border-slate-200 bg-slate-100 flex items-center justify-center text-slate-400 text-[10px]"
                                    title="Thumbnail unavailable"
                                >
                                    No preview
                                </div>
                            )}
                            {canEdit && (
                                <button
                                    type="button"
                                    className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-rose-600 text-white text-xs leading-none shadow-sm opacity-0 group-hover:opacity-100 focus:opacity-100 transition"
                                    onClick={() => confirmDelete(p)}
                                    disabled={deleteMut.isPending}
                                    title="Delete photo"
                                    aria-label="Delete photo"
                                >
                                    ×
                                </button>
                            )}
                        </div>
                    ))}
                </div>
            )}
            {canEdit && (
                <div className="mt-2 flex items-center gap-2">
                    <input
                        ref={inputRef}
                        type="file"
                        accept="image/jpeg,image/png,image/webp"
                        className="hidden"
                        onChange={(e) => void onFile(e)}
                    />
                    <button
                        type="button"
                        className="pm-btn-ghost pm-btn-sm text-brand-700"
                        onClick={() => inputRef.current?.click()}
                        disabled={uploading}
                    >
                        {uploading ? 'Uploading…' : photos.length === 0 ? 'Add photo' : 'Add another'}
                    </button>
                    {deleteMut.isPending && (
                        <span className="text-slate-400 text-xs">Deleting…</span>
                    )}
                </div>
            )}
            {uploadError && <div className="pm-error text-xs mt-1">{uploadError}</div>}
            {deleteMut.isError && (
                <div className="pm-error text-xs mt-1">
                    {deleteMut.error instanceof ApiError
                        ? deleteMut.error.message
                        : 'Delete failed.'}
                </div>
            )}
        </div>
    );
}
